import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import { getFullUrl } from '../../../../utils/url';
import { appDate } from '../../../../utils/appDateFormat';

const defaultProfile = '/images/defaultProfile.png';

interface TeamMemberCardProps {
  member?: {
    employeeName: string;
    employeeAvatar?: string;
    position: string;
    department: string;
    submittedDate?: string;
    status: string;
    completionRate: number;
  };
  onClick: () => void;
  isSelected?: boolean;
}

const TeamMemberCard: React.FC<TeamMemberCardProps> = ({
  member,
  onClick,
  isSelected = false
}) => {
  const { t } = useTranslation();

  const getStatusBadge = (status?: string) => {
    const badges: Record<string, { label: string; color: string; icon: string }> = {
      pending: { label: t('performance.managerResponse.pending'), color: 'bg-orange-100 text-orange-700', icon: 'Clock' },
      reviewed: { label: t('performance.managerResponse.responded'), color: 'bg-green-100 text-green-700', icon: 'CheckCircle' },
      not_submitted: { label: t('performance.managerResponse.notSubmitted'), color: 'bg-muted text-muted-foreground', icon: 'AlertCircle' }
    };
    return badges[status ?? 'not_submitted'] || badges.not_submitted;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return t('performance.managerResponse.notSubmitted');
    return appDate(dateString);
  };

  const getInitials = (name?: string) => {
    if (!name) return '';
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2);
  };

  const getAvatarUrl = (avatarUrl?: string): string => {
    return getFullUrl(avatarUrl) || defaultProfile;
  };

  const statusBadge = getStatusBadge(member?.status);

  return (
    <div
      onClick={onClick}
      className={`border rounded-lg p-4 cursor-pointer transition-all ${
        isSelected 
          ? 'border-primary bg-primary/5 shadow-sm' 
          : 'border-border bg-card hover:border-primary/50 hover:shadow-sm'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="flex-shrink-0">
          <img
            src={getAvatarUrl(member?.employeeAvatar)}
            alt={`${member?.employeeName} profile photo`}
            className="w-12 h-12 rounded-full object-cover"
            onError={(e) => {
              e.currentTarget.src = defaultProfile;
            }}
          />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-1">
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-foreground truncate">
                {member?.employeeName}
              </h3>
              <p className="text-xs text-muted-foreground truncate">
                {member?.position}
              </p>
            </div>
          </div>

          {/* Status Badge */}
          <div className="flex items-center gap-2 mt-2">
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusBadge.color}`}>
              <Icon name={statusBadge.icon as any} size={12} />
              <span>{statusBadge.label}</span>
            </span>
          </div>

          {/* Completion Progress */}
          {member?.status !== 'not_submitted' && (
            <div className="mt-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span>{t('performance.managerResponse.completion')}</span>
                <span className="font-medium">{member?.completionRate}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-1.5">
                <div
                  className="bg-gradient-to-r from-primary to-secondary h-1.5 rounded-full transition-all"
                  style={{ width: `${member?.completionRate}%` }}
                />
              </div>
            </div>
          )}

          {/* Submission Date */}
          <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
            <Icon name="Calendar" size={12} />
            <span>{formatDate(member?.submittedDate)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamMemberCard;